import { sendToAnalytics } from "lib/googleAnalytics";
import { BaseDeviceWrapper } from "./BaseDeviceWrapper";

import * as DeviceStates from "./DeviceStates";
import image from "images/devices/powerShot-promotion.png";
import { INTENSITIES } from "data/constants";

const serviceUuid = "00001400-0000-1000-8000-00805f9b34fb";
// Per firmware doc: control/write commands over TOUCH_CONTROL_UUID (0x1801)
const TOUCH_CONTROL_UUID = 0x1801;
const BATTERY_LEVEL_UUID = 0x2a19;
const PERIOD_DIV_50_500_MS = 0x0a;
// Low preset intensity value from INTENSITIES (second key, e.g. "25").
const LOW_INTENSITY_VALUE = Object.keys(INTENSITIES)[1];
// How much to boost the raw command when at (or below) the Low preset so it
// is clearly perceptible on PowerShot.
const LOW_INTENSITY_BOOST = 35;

const names = ["PowerShot"];

export default class PowerShot extends BaseDeviceWrapper {
  constructor(device) {
    // Use TOUCH_CONTROL for primary writes
    super(device, serviceUuid, TOUCH_CONTROL_UUID, image);
    this.ambientIntensity = 0;
    this.maxIntensity = 100;
  }

  static get deviceNames() {
    return names;
  }

  /**
   * Needed to request access to these services before connecting to the device
   */
  static get services() {
    return [serviceUuid];
  }

  get companyName() {
    return "Kiiroo";
  }

  async connect() {
    await super.connect(async () => {
      // set up battery characteristic as statusChar for getBattery
      this.statusChar = await this.sensorService.getCharacteristic(
        BATTERY_LEVEL_UUID
      );
    });
  }

  async disconnect() {
    this.setConnectionState(DeviceStates.DISCONNECTING);
    if (this.device.gatt.connected) {
      await this.device.gatt.disconnect();
    }
    this.setConnectionState(DeviceStates.MANUALLY_DISCONNECTED);
    sendToAnalytics("device_disconnect", "device", this.device.name);
  }

  async getBattery() {
    try {
      if (!this.statusChar) return -1;
      const value = await this.statusChar.readValue();
      if (value) {
        return value.getUint8(0);
      }
    } catch (e) {
      console.error("PowerShot.getBattery:", e);
    }
    return -1;
  }
  async writeCommand(data) {
    try {
      await this.motorChar.writeValue(data);
    } catch (e) {
      try {
        if (this.motorChar.writeValueWithoutResponse) {
          await this.motorChar.writeValueWithoutResponse(data);
        } else {
          throw e;
        }
      } catch (err) {
        console.error("PowerShot.writeCommand error:", err);
        throw err;
      }
    }
  }

  async writeToMotorChar(intensity) {
    // BLE command packet per doc: [mode, min1, min2, max1, max2, periodDiv50]
    // We'll use Continuous mode (0x01) where only Max Speeds are used; others set to 0x00.
    const clamped = Math.max(0, Math.min(100, Math.round(intensity)));
    if (clamped <= 0) {
      const stop = new Uint8Array([0x00]);
      await this.writeCommand(stop);
      return;
    }
    // Special handling for the "Low" preset (INTENSITIES' Low key, e.g. 25)
    // to slightly boost the command value so the vibration is clearly
    // perceptible on PowerShot.
    const maxSpeed =
      clamped <= LOW_INTENSITY_VALUE ? LOW_INTENSITY_BOOST : clamped;
    // [mode, min1, min2, max1, max2, periodDiv50]
    const data = new Uint8Array([
      0x01,
      0x00,
      0x00,
      maxSpeed,
      maxSpeed,
      PERIOD_DIV_50_500_MS,
    ]);
    await this.writeCommand(data);
  }

  async write(percent) {
    // Amplitude control only: scale by maxIntensity
    const maxIntensity = this.maxIntensity;
    const adjustedPercent = (percent * maxIntensity) / 100;
    const intensity = Math.round(Math.min(100, Math.max(0, adjustedPercent)));
    await this.writeToMotorChar(intensity);
  }
}
